import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { GarminConnectIQ } from '../plugins/garmin-connect-iq';
import { GlobalService } from './global.service';
import { UserService } from './user.service';
import { LocationSharingService } from './location-sharing.service';

@Injectable({ providedIn: 'root' })
export class GarminService implements OnDestroy {

  private destroy$ = new Subject<void>();
  private messageListener: PluginListenerHandle | null = null;

  constructor(
    private globalService: GlobalService,
    private userService: UserService,
    private locationSharing: LocationSharingService
  ) {}

  /**
   * Checks that location permission is granted ("Allow while using app" is sufficient).
   * Shows an explanation alert before prompting, and offers to open app settings if denied.
   * Returns true if permission is granted.
   */
  async ensureLocationPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return true;

    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted') return true;

    if (status.location === 'denied') {
      await this.globalService.presentAlert(
        'Location access needed',
        'connectMyClub needs location access so your Garmin watch can start live location sharing. Please enable it in app settings.',
        [{
          text: 'Open Settings',
          handler: () => {
            NativeSettings.open({
              optionAndroid: AndroidSettings.ApplicationDetails,
              optionIOS: IOSSettings.App,
            }).catch(() => {});
          },
        }, { text: 'Cancel', role: 'cancel' }]
      );
      return false;
    }

    // status.location === 'prompt' or 'prompt-with-rationale'
    await this.globalService.presentAlert(
      'Location access needed',
      'connectMyClub needs location access ("Allow only while using the app") so your Garmin watch can start live location sharing.',
      [{ text: 'Continue', role: 'confirm' }]
    );
    const result = await Geolocation.requestPermissions({ permissions: ['location'] });
    return result.location === 'granted';
  }

  async pushCredentials(): Promise<void> {
    const userId = this.userService.getUserId();
    const jwt    = this.globalService.jwttoken;

    if (!userId || !jwt) {
      throw new Error('No credentials available');
    }

    const result = await GarminConnectIQ.sendCredentials({ userId, jwt });
    if (!result.success) {
      throw new Error(result.message === 'no_devices' ? 'no_devices' : result.message);
    }
    console.log('[Garmin] Credentials pushed:', result.message);
  }

  async startListening(): Promise<void> {
    if (this.messageListener) return;

    this.messageListener = await GarminConnectIQ.addListener('watchMessage', async (msg) => {
      console.log('[Garmin] Watch message received:', msg);
      try {
        if (msg.action === 'startSharing') {
          const jwt    = this.globalService.jwttoken;
          const userId = this.userService.getUserId();

          // Start the native location service — it handles all HTTP posting and
          // keeps running in the background without the Capacitor Geolocation CLLocationManager.
          if (jwt && userId) {
            await GarminConnectIQ.startLocationSharing({ jwt, userId });
            console.log('[Garmin] Native location service started');
          }

          // Update isSharing$ state for the UI without starting a JS watchPosition.
          // If we started watchPosition here, Capacitor Geolocation's CLLocationManager
          // would keep the app alive in background and continue posting after the native
          // plugin stops — causing sharing to persist after the watch sends stopSharing.
          await this.locationSharing.start(false);
          console.log('[Garmin] JS location sharing started');

        } else if (msg.action === 'stopSharing') {
          // Native registerMessageListener already called stopLocationService() before notifyListeners —
          // calling stopLocationSharing() here again would create an ACTION_STOP → onLocationSharingStopped
          // → stopSharing event loop. Just clean up JS state.
          await this.locationSharing.stop().catch(() => {});
          console.log('[Garmin] Location sharing stopped by watch');

        } else if (msg.action === 'sharingStopped') {
          // Service stopped internally (notification tap, OS kill, etc.) — update JS state only.
          await this.locationSharing.stop().catch(() => {});
          console.log('[Garmin] Location sharing stopped by service');

        } else if (msg.action === 'sharingFailed') {
          await this.locationSharing.stop().catch(() => {});
          this.globalService.presentToast('Location sharing failed — please check location permissions', 4000, 'bottom');
          console.warn('[Garmin] Location sharing failed (permissions denied)');
        }
      } catch (err) {
        console.warn('[Garmin] Failed to handle watch message:', err);
      }
    });
  }

  get isSharing(): boolean {
    return this.locationSharing.sharing;
  }

  get isSharing$() {
    return this.locationSharing.sharing$;
  }

  async startSharing(): Promise<void> {
    const permitted = await this.ensureLocationPermission();
    if (!permitted) return;

    const jwt    = this.globalService.jwttoken;
    const userId = this.userService.getUserId();
    if (jwt && userId) {
      await GarminConnectIQ.startLocationSharing({ jwt, userId });
    }
    await this.locationSharing.start();
  }

  async stopSharing(): Promise<void> {
    await GarminConnectIQ.stopLocationSharing();
    await this.locationSharing.stop();
  }

  /** Called when the app returns to the foreground. Auto-starts sharing if the watch requested it while the app was backgrounded. */
  async resumePendingShare(): Promise<void> {
    const { resumed } = await GarminConnectIQ.resumePendingShare();
    if (resumed) {
      // Native plugin handles posting — update state only, don't start watchPosition.
      await this.locationSharing.start(false);
      console.log('[Garmin] Pending share request fulfilled on foreground');
    }
  }

  async stopListening(): Promise<void> {
    await GarminConnectIQ.stopLocationSharing().catch(() => {});
    await this.locationSharing.stop().catch(() => {});
    if (this.messageListener) {
      await this.messageListener.remove();
      this.messageListener = null;
    }
  }

  ngOnDestroy(): void {
    // Only remove the JS listener — do NOT stop the native location service here.
    // stopListening() is for explicit logout; ngOnDestroy fires if Angular destroys
    // the root injector (rare), and we must not kill an in-progress background share.
    if (this.messageListener) {
      this.messageListener.remove().catch(() => {});
      this.messageListener = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
